import React from 'react';
import {Button, EMPHASIS, Icon, PopoverHandler} from '@forecast-it/design-system';
import Styled from 'styled-components';

export interface MissingDatePopoverProps {
	// The red "…date missing" label shown inline next to the "?" trigger.
	label: string;
	// Popover title, body and primary-action-button copy.
	title: string;
	body: string;
	actionLabel: string;
	// Accessible label for the close (✕) button.
	closeLabel: string;
	// data-cy for the "?" trigger.
	triggerDataCy: string;
	// Opens the shared Add-Dates modal (fired by the primary action button).
	onAddDates: () => void;
}

// Colours/spacing follow the design-system tokens: error red (#db0900) accent + label, content-main text
// (#232334), high-emphasis primary action (#0004db), and the popover surface's 8px radius.
// min-width: 0 so the label can actually shrink - a flex item defaults to min-width auto, which
// refuses to go narrower than its content.
const Row = Styled.span`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	min-width: 0;
	max-width: 100%;
`;
// This label is the widest thing either date column ever holds - wider than any date - and the
// table cell it sits in has `white-space: nowrap` with no containment of its own, so without
// this it spills over the adjacent columns. It clips rather than the "?" beside it, which stays
// visible and opens the popover explaining the missing date in full. Where it does clip, the
// `title` on the element carries the whole text on hover - the same pairing ContactColumn and
// LinkColumn use for their truncated cells.
const RedLabel = Styled.span`
	color: #db0900;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	min-width: 0;
`;
const HelpTrigger = Styled.button`
	display: inline-flex;
	align-items: center;
	padding: 0;
	border: none;
	background: none;
	cursor: pointer;
	color: #71717a;
`;

// DS Popover.Content already supplies the white surface, thin border, 8px radius and the arrow; here we add
// the drop shadow, a fixed width, the red left accent bar and the padded content layout.
const Card = Styled.div`
	position: relative;
	overflow: hidden;
	width: 320px;
	border-radius: 8px;
	box-shadow: 0 4px 16px rgba(35, 35, 52, 0.12);
`;
const Accent = Styled.span`
	position: absolute;
	left: 0;
	top: 0;
	bottom: 0;
	width: 4px;
	background: #db0900;
`;
const Inner = Styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
	padding: 16px 16px 16px 20px;
`;
const Header = Styled.div`
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: 8px;
`;
const Title = Styled.span`
	color: #232334;
	font-weight: 500;
	font-size: 14px;
	line-height: 20px;
`;
const CloseButton = Styled.button`
	display: inline-flex;
	padding: 0;
	border: none;
	background: none;
	cursor: pointer;
	color: #232334;
`;
const Body = Styled.p`
	margin: 0;
	color: #232334;
	font-size: 14px;
	line-height: 20px;
`;

// The card body needs the popover's open state to close itself, so it lives in its own component that runs
// inside PopoverHandler's context (where usePopoverState is available).
const MissingDatePopoverCard = ({
	title,
	body,
	actionLabel,
	closeLabel,
	onAddDates,
}: Pick<MissingDatePopoverProps, 'title' | 'body' | 'actionLabel' | 'closeLabel' | 'onAddDates'>): JSX.Element => {
	const {setOpen} = PopoverHandler.usePopoverState();
	return (
		<Card data-cy="missing-date-popover">
			<Accent />
			<Inner>
				<Header>
					<Title>{title}</Title>
					<CloseButton
						type="button"
						aria-label={closeLabel}
						data-cy="missing-date-popover-close"
						onClick={() => setOpen(false)}
					>
						<Icon icon="close" size="s" />
					</CloseButton>
				</Header>
				<Body>{body}</Body>
				<div>
					<Button
						emphasis={EMPHASIS.HIGH}
						icon="plus"
						data-cy="missing-date-popover-add"
						onPress={() => {
							setOpen(false);
							onAddDates();
						}}
					>
						{actionLabel}
					</Button>
				</div>
			</Inner>
		</Card>
	);
};

export const MissingDatePopover = ({
	label,
	title,
	body,
	actionLabel,
	closeLabel,
	triggerDataCy,
	onAddDates,
}: MissingDatePopoverProps): JSX.Element => (
	<Row>
		<RedLabel title={label}>{label}</RedLabel>
		<PopoverHandler>
			<PopoverHandler.Trigger>
				<HelpTrigger type="button" data-cy={triggerDataCy} aria-label={title}>
					<Icon icon="help" size="s" />
				</HelpTrigger>
			</PopoverHandler.Trigger>
			<PopoverHandler.Content showArrow>
				<MissingDatePopoverCard
					title={title}
					body={body}
					actionLabel={actionLabel}
					closeLabel={closeLabel}
					onAddDates={onAddDates}
				/>
			</PopoverHandler.Content>
		</PopoverHandler>
	</Row>
);

export default MissingDatePopover;
